import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Circle,
  ClipboardCheck,
  ListTree,
  Lock,
  X,
} from 'lucide-react';
import {
  useApi,
  type CourseAssessmentState,
  type LearnerCourse,
  type LessonRequirements,
  type PublicCourse,
} from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDateTime } from '@/lib/dates';
import { Badge, Button, Card, ErrorText } from '@/components/ui';
import { MarkdownContent } from '@/components/course/markdown';
import { ShareQuizCard } from '@/components/quiz/share-quiz';
import { OfferedBy } from '@/components/org-brand';

// The renderers (Markdown, PDF, notebooks…) load only on lesson pages.
const LessonBlocks = lazy(() =>
  import('@/components/course/lesson-blocks').then((m) => ({ default: m.LessonBlocks })),
);

const LEVEL: Record<string, string> = {
  beginner: 'Beginner',
  intermediate: 'Intermediate',
  advanced: 'Advanced',
};
const minutes = (m: number | null) =>
  m ? (m >= 90 ? `${Math.round(m / 60)} hours` : `${m} min`) : null;

/** The course page: what it covers, then Enroll or Continue with progress. */
export function CoursePage() {
  const { slug = '' } = useParams();
  const api = useApi();
  const { user, loading } = useAuth();
  const details = useQuery({
    queryKey: ['publicCourse', slug],
    queryFn: () => api.publicCourse(slug),
    retry: false,
  });
  const state = useQuery({
    queryKey: ['learnerCourse', slug],
    queryFn: () => api.learnerCourse(slug),
    enabled: Boolean(user),
    retry: false,
  });
  if (details.isPending || loading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (details.isError) return <ErrorText error={details.error} />;
  const c = details.data;
  const enrolled = state.data ?? null;
  return (
    <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <div className="min-w-0 space-y-6">
        <Card className={c.coverUrl ? 'overflow-hidden pt-0' : undefined}>
          {c.coverUrl && (
            <img
              src={c.coverUrl}
              alt=""
              className="-mx-5 mb-5 aspect-[2/1] w-[calc(100%+2.5rem)] max-w-none object-cover"
            />
          )}
          {c.offeredBy ? (
            <OfferedBy org={c.offeredBy} />
          ) : (
            <p className="text-sm text-muted-foreground">{c.issuerName}</p>
          )}
          <h1 className="mt-1 text-2xl font-semibold">{c.title}</h1>
          {c.summary && <p className="mt-2 text-muted-foreground">{c.summary}</p>}
          <ul className="mt-4 flex flex-wrap gap-2">
            <Badge>{LEVEL[c.level] ?? c.level}</Badge>
            <Badge>{c.modules.reduce((n, m) => n + m.lessons.length, 0)} lessons</Badge>
            {minutes(c.estimatedMinutes) && <Badge>{minutes(c.estimatedMinutes)}</Badge>}
            {c.hasFinalQuiz && <Badge>Final assessment</Badge>}
            {c.certificate && <Badge>Certificate</Badge>}
          </ul>
          <div className="mt-6">
            <EnrollAction slug={slug} details={c} enrolled={enrolled} signedIn={Boolean(user)} />
          </div>
        </Card>
        {enrolled && <ProgressCard course={enrolled} />}
        {c.outcomes.length > 0 && (
          <Card title="What you'll learn">
            <ul className="space-y-2">
              {c.outcomes.map((o) => (
                <li key={o} className="flex gap-2 text-sm">
                  <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
                  {o}
                </li>
              ))}
            </ul>
          </Card>
        )}
        {c.description && (
          <Card title="About this course">
            <MarkdownContent markdown={c.description} />
          </Card>
        )}
      </div>
      <aside className="min-w-0 space-y-6">
        <Card title="Course outline">
          <Outline slug={slug} details={c} enrolled={enrolled} />
        </Card>
        <ShareQuizCard
          kind="course"
          slug={slug}
          title={c.title}
          audience={c.audience}
          heading="Help others get ready"
          text={
            c.audience === 'public'
              ? 'Share this course with friends, classmates and colleagues, or run a campaign with printable posters for notice boards and common areas.'
              : 'Run a campaign in your organization with printable posters for notice boards and common areas.'
          }
        />
      </aside>
    </div>
  );
}

function EnrollAction({
  slug,
  details,
  enrolled,
  signedIn,
}: {
  slug: string;
  details: PublicCourse;
  enrolled: LearnerCourse | null;
  signedIn: boolean;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const enroll = useMutation({
    mutationFn: () => api.enroll(slug),
    onSuccess: (state) => {
      queryClient.setQueryData(['learnerCourse', slug], state);
      const first = state.nextLessonId ?? details.modules[0]?.lessons[0]?.id;
      if (first) navigate(`/c/${slug}/learn/${first}`);
    },
  });
  if (!signedIn)
    return (
      <Link
        to={`/signin?next=${encodeURIComponent(`/c/${slug}`)}`}
        className="flex min-h-11 w-full items-center justify-center rounded-[var(--radius)] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
      >
        Sign in to start
      </Link>
    );
  if (!enrolled)
    return (
      <>
        <Button className="w-full" disabled={enroll.isPending} onClick={() => enroll.mutate()}>
          {enroll.isPending ? 'Starting…' : 'Start the course'}
        </Button>
        <ErrorText error={enroll.error} />
      </>
    );
  const target =
    enrolled.nextLessonId ?? enrolled.lastLessonId ?? enrolled.modules[0]?.lessons[0]?.id;
  return target ? (
    <Link
      to={`/c/${slug}/learn/${target}`}
      className="flex min-h-11 w-full items-center justify-center rounded-[var(--radius)] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
    >
      {enrolled.completedAt
        ? 'Review the course'
        : enrolled.progress.completed > 0
          ? 'Continue'
          : 'Start the first lesson'}
    </Link>
  ) : null;
}

function ProgressCard({ course }: { course: LearnerCourse }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const upgrade = useMutation({
    mutationFn: () => api.upgradeCourse(course.slug),
    onSuccess: (state) => queryClient.setQueryData(['learnerCourse', course.slug], state),
  });
  const { completed, required } = course.progress;
  return (
    <Card title="Your progress">
      <div className="space-y-4">
        <div>
          <div className="mb-1 flex justify-between text-sm">
            <span>
              {completed} of {required} required lesson{required === 1 ? '' : 's'} complete
            </span>
            <span className="tabular-nums text-muted-foreground">
              {required ? Math.round((completed / required) * 100) : 0}%
            </span>
          </div>
          <progress
            max={required || 1}
            value={completed}
            aria-label="Course progress"
            className="h-2 w-full appearance-none overflow-hidden rounded-full [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary"
          />
        </div>
        {course.finalQuiz && <AssessmentRow slug={course.slug} state={course.finalQuiz} />}
        {course.completedAt && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-primary/10 p-3 text-sm">
            <span className="flex items-center gap-2 font-medium">
              <Award className="size-4 text-primary" aria-hidden /> Course complete
            </span>
            {course.certificateCode && (
              <Link to="/certificates" className="-my-2 py-2 font-medium text-primary">
                Your certificate
              </Link>
            )}
          </div>
        )}
        {course.newerVersion && (
          <div className="rounded-lg border border-border p-3 text-sm">
            <p>A newer version of this course is available. Your progress carries over.</p>
            <Button
              variant="secondary"
              className="mt-2"
              disabled={upgrade.isPending}
              onClick={() => upgrade.mutate()}
            >
              Switch to the new version
            </Button>
            <ErrorText error={upgrade.error} />
          </div>
        )}
      </div>
    </Card>
  );
}

function Outline({
  slug,
  details,
  enrolled,
  current,
  onPick,
}: {
  slug: string;
  details: PublicCourse | LearnerCourse;
  enrolled: LearnerCourse | null;
  current?: string;
  onPick?: () => void;
}) {
  const modules = enrolled?.modules ?? details.modules;
  return (
    <ol className="space-y-5">
      {modules.map((m, mi) => (
        <li key={m.id}>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {m.title === `Module ${mi + 1}` ? m.title : `Module ${mi + 1} · ${m.title}`}
          </p>
          <ol className="mt-2 space-y-1">
            {m.lessons.map((l) => {
              const done = 'completed' in l && l.completed;
              const body = (
                <span className="flex items-start gap-2">
                  {done ? (
                    <CheckCircle2
                      className="mt-0.5 size-4 shrink-0 text-india-green"
                      aria-label="Completed"
                    />
                  ) : (
                    <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  )}
                  <span className="min-w-0">
                    <span className="block">{l.title}</span>
                    {(l.optional || l.estimatedMinutes) && (
                      <span className="block text-xs text-muted-foreground">
                        {[l.optional && 'Optional', minutes(l.estimatedMinutes)]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    )}
                  </span>
                </span>
              );
              return (
                <li key={l.id}>
                  {enrolled ? (
                    <Link
                      to={`/c/${slug}/learn/${l.id}`}
                      onClick={onPick}
                      aria-current={current === l.id ? 'page' : undefined}
                      className={`block rounded-md px-2 py-2 text-sm ${current === l.id ? 'bg-primary/10 font-medium' : 'hover:bg-muted'}`}
                    >
                      {body}
                    </Link>
                  ) : (
                    <div className="px-2 py-2 text-sm">{body}</div>
                  )}
                </li>
              );
            })}
          </ol>
        </li>
      ))}
      {(enrolled ? enrolled.finalQuiz : 'hasFinalQuiz' in details && details.hasFinalQuiz) && (
        <li>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Final assessment
          </p>
          <FinalOutlineItem slug={slug} state={enrolled?.finalQuiz ?? null} onPick={onPick} />
        </li>
      )}
    </ol>
  );
}

/** The last outline entry: the final assessment, locked until the required lessons are done. */
function FinalOutlineItem({
  slug,
  state,
  onPick,
}: {
  slug: string;
  state: CourseAssessmentState | null;
  onPick?: () => void;
}) {
  const open = state?.unlocked && !state.passed;
  const body = (
    <span className="flex items-start gap-2">
      {state?.passed ? (
        <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-india-green" aria-label="Passed" />
      ) : open ? (
        <ClipboardCheck className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
      ) : (
        <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-label="Locked" />
      )}
      <span className="min-w-0">
        <span className="block">{state?.title ?? 'Final assessment'}</span>
        <span className="block text-xs text-muted-foreground">
          {state?.passed
            ? `Passed (${state.bestPercent}%)`
            : open
              ? `${state.questionCount} questions`
              : 'Unlocks when you finish the required lessons'}
        </span>
      </span>
    </span>
  );
  return open ? (
    <Link
      to={`/c/${slug}/assessment`}
      onClick={onPick}
      className="mt-2 block rounded-md px-2 py-2 text-sm hover:bg-muted"
    >
      {body}
    </Link>
  ) : (
    <div className="mt-2 px-2 py-2 text-sm">{body}</div>
  );
}

// ─── Lesson player ─────────────────────────────────────────────────────

export function LessonPlayerPage() {
  const { slug = '', lessonId = '' } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const course = useQuery({
    queryKey: ['learnerCourse', slug],
    queryFn: () => api.learnerCourse(slug),
    retry: false,
  });
  const lesson = useQuery({
    queryKey: ['learnerLesson', slug, lessonId],
    queryFn: () => api.learnerLesson(slug, lessonId),
    retry: false,
  });
  const [requirements, setRequirements] = useState<LessonRequirements | null>(null);
  const [outlineOpen, setOutlineOpen] = useState(false);
  const top = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRequirements(lesson.data?.requirements ?? null);
    top.current?.scrollIntoView({ block: 'start' });
  }, [lesson.data]);

  // Media progress, batched: the player reports at most every few seconds.
  const pending = useRef<Record<string, number>>({});
  const flush = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMediaProgress = useCallback(
    (blockId: string, ratio: number) => {
      pending.current[blockId] = Math.max(pending.current[blockId] ?? 0, ratio);
      if (flush.current) return;
      flush.current = setTimeout(async () => {
        const media = pending.current;
        pending.current = {};
        flush.current = null;
        try {
          setRequirements(await api.lessonMedia(slug, lessonId, media));
        } catch {
          // Progress is best-effort; the next report retries.
        }
      }, 1000);
    },
    [api, slug, lessonId],
  );
  const onCheckAnswer = useCallback(
    async (blockId: string, questionId: string, optionIds: string[]) => {
      const r = await api.lessonCheck(slug, lessonId, { blockId, questionId, optionIds });
      setRequirements(r.requirements);
      return r;
    },
    [api, slug, lessonId],
  );
  const complete = useMutation({
    mutationFn: () => api.completeLesson(slug, lessonId),
    onSuccess: (state) => {
      queryClient.setQueryData(['learnerCourse', slug], state);
      void queryClient.invalidateQueries({ queryKey: ['home'] });
      void queryClient.invalidateQueries({ queryKey: ['catalog'] });
      const next = lesson.data?.nextLessonId;
      const final = state.finalQuiz;
      navigate(
        next && !state.completedAt
          ? `/c/${slug}/learn/${next}`
          : // Lessons done: on to the final assessment if it's open.
            final?.unlocked && !final.passed && !final.retryAt
            ? `/c/${slug}/assessment`
            : `/c/${slug}`,
      );
    },
  });

  if (course.isError) return <ErrorText error={course.error} />;
  // Not enrolled (e.g. a shared lesson link): the course page offers enrollment.
  if (course.isSuccess && !course.data) return <Navigate to={`/c/${slug}`} replace />;
  if (lesson.isError) return <ErrorText error={lesson.error} />;
  if (course.isPending || lesson.isPending)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  const l = lesson.data;
  const c = course.data!;
  const done = Boolean(c.modules.flatMap((m) => m.lessons).find((x) => x.id === l.id)?.completed);
  const req = requirements ?? l.requirements;

  return (
    <div ref={top} className="scroll-mt-20 lg:grid lg:grid-cols-[18rem_1fr] lg:gap-8">
      {/* Outline: sidebar on wide screens, a drawer on phones. */}
      <aside className="hidden lg:block">
        <div className="sticky top-6 max-h-[calc(100dvh-3rem)] overflow-y-auto">
          <Link
            to={`/c/${slug}`}
            className="-my-2 block py-2 text-sm font-medium hover:text-primary"
          >
            {c.title}
          </Link>
          <p className="mb-4 text-xs text-muted-foreground">
            {c.progress.completed} of {c.progress.required} complete
          </p>
          <Outline slug={slug} details={c} enrolled={c} current={l.id} />
        </div>
      </aside>
      {outlineOpen && (
        <div
          className="fixed inset-0 z-30 bg-background lg:hidden"
          role="dialog"
          aria-label="Course outline"
        >
          <div className="flex h-14 items-center justify-between border-b border-border px-4">
            <span className="min-w-0 truncate font-medium">{c.title}</span>
            <button
              type="button"
              aria-label="Close outline"
              className="flex size-11 items-center justify-center"
              onClick={() => setOutlineOpen(false)}
            >
              <X className="size-5" aria-hidden />
            </button>
          </div>
          <div className="h-[calc(100dvh-3.5rem)] overflow-y-auto p-4">
            <Outline
              slug={slug}
              details={c}
              enrolled={c}
              current={l.id}
              onPick={() => setOutlineOpen(false)}
            />
          </div>
        </div>
      )}

      <article className="min-w-0 space-y-6 pb-28">
        <div className="flex items-center justify-between gap-3 lg:hidden">
          <Link
            to={`/c/${slug}`}
            className="-my-2 min-w-0 truncate py-2 text-sm text-muted-foreground"
          >
            ← {c.title}
          </Link>
          <Button variant="secondary" onClick={() => setOutlineOpen(true)}>
            <ListTree className="size-4" aria-hidden /> Outline
          </Button>
        </div>
        <header>
          <h1 className="text-2xl font-semibold">{l.title}</h1>
          {l.summary && <p className="mt-1 text-muted-foreground">{l.summary}</p>}
          <div className="mt-2 flex flex-wrap gap-2">
            {l.optional && <Badge>Optional</Badge>}
            {minutes(l.estimatedMinutes) && <Badge>{minutes(l.estimatedMinutes)}</Badge>}
            {done && <Badge>Completed</Badge>}
          </div>
        </header>
        <Suspense fallback={<p className="text-sm text-muted-foreground">Loading lesson…</p>}>
          <LessonBlocks
            blocks={l.blocks}
            assets={l.assets}
            initialProgress={l.progress}
            onMediaProgress={onMediaProgress}
            onCheckAnswer={onCheckAnswer}
          />
        </Suspense>
      </article>

      {/* Sticky footer: navigation and completion within thumb reach. */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-background/95 px-4 py-3 backdrop-blur">
        {/* Same grid as the page, so the buttons sit under the lesson, beside the outline. */}
        <div className="mx-auto max-w-5xl lg:grid lg:grid-cols-[18rem_1fr] lg:gap-8">
          <span className="hidden lg:block" />
          <div className="flex items-center gap-2">
            {l.previousLessonId ? (
              <Link
                to={`/c/${slug}/learn/${l.previousLessonId}`}
                aria-label="Previous lesson"
                className="flex size-11 shrink-0 items-center justify-center rounded-[var(--radius)] border border-border hover:bg-muted"
              >
                <ChevronLeft className="size-5" aria-hidden />
              </Link>
            ) : (
              <span className="size-11 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              {done ? (
                <Link
                  to={l.nextLessonId ? `/c/${slug}/learn/${l.nextLessonId}` : `/c/${slug}`}
                  className="flex min-h-11 w-full items-center justify-center rounded-[var(--radius)] bg-primary px-4 text-sm font-medium text-primary-foreground hover:opacity-90"
                >
                  {l.nextLessonId ? 'Next lesson' : 'Back to the course'}
                </Link>
              ) : (
                <Button
                  className="min-h-11 w-full"
                  disabled={!req.canComplete || complete.isPending}
                  onClick={() => complete.mutate()}
                >
                  {complete.isPending
                    ? 'Saving…'
                    : l.nextLessonId
                      ? 'Complete and continue'
                      : 'Complete the lesson'}
                </Button>
              )}
              {!done && !req.canComplete && (
                <p className="mt-1 text-center text-xs text-muted-foreground">
                  {req.mediaLeft.length ? 'Watch or listen to the end first. ' : ''}
                  {req.checksLeft.length ? 'Answer the knowledge check first.' : ''}
                </p>
              )}
              <ErrorText error={complete.error} />
            </div>
            {l.nextLessonId ? (
              <Link
                to={`/c/${slug}/learn/${l.nextLessonId}`}
                aria-label="Next lesson"
                className="flex size-11 shrink-0 items-center justify-center rounded-[var(--radius)] border border-border hover:bg-muted"
              >
                <ChevronRight className="size-5" aria-hidden />
              </Link>
            ) : (
              <span className="size-11 shrink-0" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The course's final assessment: locked until the required lessons are done. */
export function AssessmentRow({ slug, state }: { slug: string; state: CourseAssessmentState }) {
  const status = state.passed
    ? `Passed (${state.bestPercent}%)`
    : !state.unlocked
      ? `Unlocks when you finish the required lessons (${state.requiredLeft} to go)`
      : state.retryAt
        ? `Try again after ${formatDateTime(state.retryAt)}`
        : state.attempted
          ? `Best ${state.bestPercent}% so far; ${state.passPercent}% to pass`
          : `${state.questionCount} questions; ${state.passPercent}% to pass`;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3 text-sm">
      <span className="flex min-w-0 items-start gap-2">
        {state.unlocked || state.passed ? (
          <ClipboardCheck className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
        ) : (
          <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <span>
          <span className="block font-medium">Final assessment</span>
          <span className="text-muted-foreground">{status}</span>
        </span>
      </span>
      {state.unlocked && !state.passed && !state.retryAt && (
        <Link
          to={`/c/${slug}/assessment`}
          className="w-full rounded-[var(--radius)] bg-primary px-4 py-2 text-center font-medium text-primary-foreground hover:opacity-90 sm:w-auto"
        >
          {state.attempted ? 'Try again' : 'Take the assessment'}
        </Link>
      )}
    </div>
  );
}
